/**
 * SP Quote pricing — salesman negotiation bands:
 *
 *   🟢 Green  = website retail (published grid, COGS-adjusted)
 *               fallback: $350/hr when no retail tier applies
 *   🟡 Yellow = press-rate competitive floor, per decoration method:
 *               screen $450/hr · DTF $250/hr · embroidery $250/hr
 *               (DTF/embroidery run with 1 operator vs 2.5–3 on the screen press)
 *   🔴 Red    = $325/hr "only with intent" (never above the floor)
 *
 * Minutes = decoration-specific production time
 *   screen: max(Stats minutes, print-list minutes)
 * Price for press-rate bands:
 *   price = cogs + (targetRate × minutes/60) / qty
 */

import {
  calculateScreenPrintTime,
  calculateDTFTime,
  calculateEmbroideryTime,
} from '../../utils/printListCalculations';
import { lookupRetailPrice, EMBROIDERY_STITCH_TIERS } from './retailPricing';

export const PRESS_RATES = {
  /** Used only when retail grid has no tier for this job */
  greenFallback: 350,
  yellow: 450,
  red: 325,
};

/** Yellow floor $/hr by decoration method (screen uses PRESS_RATES.yellow). */
export const FLOOR_RATES = {
  screen: PRESS_RATES.yellow,
  dtf: 250,
  embroidery: 250,
};

export function floorRateFor(decorationType) {
  return FLOOR_RATES[decorationType] || PRESS_RATES.yellow;
}

/** Stats-only minutes (screen-centric): inkCount×15 + qty/4 */
export function calculateStatsMinutes(quantity, inkCount) {
  const qty = Number(quantity) || 0;
  const colors = Number(inkCount) || 0;
  if (qty <= 0) return 0;
  return colors * 15 + qty / 4;
}

function locationColorCounts(locations = {}) {
  const front = locations.front?.enabled ? Math.max(0, parseInt(locations.front.colors, 10) || 0) : 0;
  const back = locations.back?.enabled ? Math.max(0, parseInt(locations.back.colors, 10) || 0) : 0;
  const other = locations.other?.enabled ? Math.max(0, parseInt(locations.other.colors, 10) || 0) : 0;
  return { front, back, other };
}

function enabledLocationCount(locations = {}) {
  return ['front', 'back', 'other'].reduce((n, key) => (
    locations[key]?.enabled ? n + 1 : n
  ), 0);
}

/**
 * Production + Stats hybrid minutes for a quote line.
 * Returns { minutes, statsMinutes, productionMinutes, method }.
 */
export function calculateHybridMinutes({ quantity, decorationType, locations }) {
  const qty = Number(quantity) || 0;
  if (qty <= 0) {
    return { minutes: 0, statsMinutes: 0, productionMinutes: 0, method: decorationType || 'screen' };
  }

  const { front, back, other } = locationColorCounts(locations);
  const totalColors = front + back + other;
  const locationCount = enabledLocationCount(locations);
  const statsMinutes = calculateStatsMinutes(qty, totalColors || locationCount);

  const type = decorationType || 'screen';

  if (type === 'dtf') {
    const productionMinutes = calculateDTFTime(qty, Math.max(1, locationCount));
    return {
      minutes: productionMinutes,
      statsMinutes,
      productionMinutes,
      method: 'dtf',
    };
  }

  if (type === 'embroidery') {
    // Sum stitch estimates across enabled locations (website tier midpoints)
    const stitchesPerPiece = ['front', 'back', 'other'].reduce((sum, key) => {
      if (!locations?.[key]?.enabled) return sum;
      const tierValue = locations[key]?.stitchTier || 'under5k';
      const meta = EMBROIDERY_STITCH_TIERS.find((t) => t.value === tierValue);
      return sum + (meta?.stitches || 5000);
    }, 0);
    const productionMinutes = calculateEmbroideryTime(
      qty,
      stitchesPerPiece > 0 ? stitchesPerPiece : undefined
    );
    return {
      minutes: productionMinutes,
      statsMinutes,
      productionMinutes,
      method: 'embroidery',
    };
  }

  const productionMinutes = calculateScreenPrintTime(qty, front, back, other);
  const minutes = Math.max(statsMinutes, productionMinutes);
  return {
    minutes,
    statsMinutes,
    productionMinutes,
    method: 'screen',
  };
}

/**
 * Minimum sell price ($/pc) to hit a target press rate given known minutes.
 * price = cogs + (rate * hours) / quantity
 */
export function priceForPressRate(cogs, quantity, minutes, targetRate) {
  const qty = Number(quantity) || 0;
  const garmentCost = Number(cogs) || 0;
  const mins = Number(minutes) || 0;
  if (qty <= 0 || !targetRate || mins <= 0) return null;

  const hours = mins / 60;
  const decorationPerPiece = (targetRate * hours) / qty;
  return garmentCost + decorationPerPiece;
}

export function formatMinutes(minutes) {
  const total = Math.round(Number(minutes) || 0);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

export function calculateQuoteBands({ quantity, cogs, decorationType, locations }) {
  const qty = Number(quantity) || 0;
  const garmentCost = Number(cogs) || 0;
  const locationCount = enabledLocationCount(locations);

  if (qty <= 0 || garmentCost < 0 || cogs === '' || cogs == null) {
    return null;
  }

  const timing = calculateHybridMinutes({ quantity: qty, decorationType, locations });
  if (timing.minutes <= 0) return null;

  const yellowRate = floorRateFor(decorationType);
  const redRate = Math.min(PRESS_RATES.red, yellowRate);
  const yellow = priceForPressRate(garmentCost, qty, timing.minutes, yellowRate);
  const red = priceForPressRate(garmentCost, qty, timing.minutes, redRate);
  if (yellow == null || red == null) return null;

  const retail = lookupRetailPrice({
    decorationType,
    quantity: qty,
    locationCount,
    locations,
    cogs: garmentCost,
  });

  let green = null;
  let greenSource;
  let greenRate = null;
  let retailMeta = null;
  let greenUnavailableReason = null;

  if (retail) {
    green = retail.perPiece;
    greenSource = 'retail';
    retailMeta = retail;
  } else if (decorationType === 'screen') {
    green = null;
    greenSource = 'unavailable';
    greenUnavailableReason = 'Unavailable — try DTF';
  } else if (decorationType === 'embroidery') {
    green = null;
    greenSource = 'unavailable';
    greenUnavailableReason =
      qty > 150 ? 'Unavailable — get in touch' : 'Unavailable — check qty / locations';
  } else if (decorationType === 'dtf') {
    green = null;
    greenSource = 'unavailable';
    greenUnavailableReason = 'Unavailable — get in touch';
  } else {
    green = priceForPressRate(garmentCost, qty, timing.minutes, PRESS_RATES.greenFallback);
    greenSource = 'pressRate';
    greenRate = PRESS_RATES.greenFallback;
    if (green == null) return null;
  }

  const setupFee = retailMeta?.setupFee || 0;

  return {
    ...timing,
    locationCount,
    timeLabel: formatMinutes(timing.minutes),
    greenSource,
    greenUnavailableReason,
    retail: retailMeta,
    bands: {
      green: {
        perPiece: green,
        total: green == null ? null : green * qty,
        rate: greenRate,
        source: greenSource,
        setupFee,
        unavailableReason: greenUnavailableReason,
      },
      yellow: {
        perPiece: yellow,
        total: yellow * qty,
        rate: yellowRate,
        setupFee: 0,
      },
      red: {
        perPiece: red,
        total: red * qty,
        rate: redRate,
        setupFee: 0,
      },
    },
  };
}

export function money(n) {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}`;
}
